'use client';

import { useCallback, useMemo } from 'react';
import { useParams } from 'next/navigation';
import type { Locale } from '@/types/dictionary';
import { DotNestedKeys } from '@/utils/locale/getTranslations';
import getLocaleFunc from '@/utils/locale/getLocale';
import { get } from 'lodash';
import replaceParams from '@/utils/locale/replaceParams';
import dictionaries from '@/constants/dictionaries';
import { DEFAULT_LOCALE } from '@/constants/locale';

type Dictionaries = typeof dictionaries;

export default function useTranslations() {
  const params = useParams<{ locale?: Locale }>();
  // The Studio's routes carry no locale segment; its tools read in the default language.
  const locale = params?.locale ?? DEFAULT_LOCALE;

  const dictionary = useMemo(() => dictionaries?.[locale] || {}, [locale]);

  const getLocale = useCallback(
    (value: string | Record<Locale, string>) => getLocaleFunc(value, locale),
    [locale],
  );

  const tFunction = useCallback(
    (path: DotNestedKeys<Dictionaries['en']>, keys: Record<string, string> = {}): string => {
      const translation = get(dictionary, path, path)!;
      return replaceParams(translation, keys);
    },
    [dictionary],
  );

  return {
    t: tFunction,
    getLocale,
    dictionary,
    locale,
  };
}
